import React from 'react';
const { useState, useEffect, useRef } = React;
import { inputSt, labelSt, cardSt, mutedSt, badgeSt, warnSt, fmtDate } from '../diagnostic/shared.jsx';
import { TASK_PRIORITIES, DAY_TEXT_LIMITS as DL, PLAN_LIMITS as PL, PLAN_SAVE_MAX, GOAL_AREAS, parseMinutes } from '../../ownerSpaceConfig.js';
import { ownerCall, Notice } from './shared.jsx';

// Mi día · "De un objetivo a tareas". I write what I want to achieve or do,
// choose the context the AI may see, and the AI proposes a short sequence of
// tasks with an area each. It is a suggestion until I confirm: I edit, delete,
// add, reorder or move tasks to another area, set dates, priorities and
// estimates, choose which to keep and confirm. Only then does the server save
// them as my tasks (shared/ownerPlan.ts), once per step even with a double
// click or a retry. Nothing is booked and no message is sent.

const smallBtn = { fontSize: 11, padding: '2px 8px' };
const rowSt = { border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, margin: '8px 0' };
const suggestionSt = { ...badgeSt(false), background: 'rgba(191,215,48,0.18)', color: '#5C6A12' };
const savedSt = { ...badgeSt(true) };

const rowFrom = (t) => ({
  key: t.key, title: t.title, notes: t.detail || '', category: t.area || '', newArea: t.newArea || '', priority: 'normal', dueOn: '',
  estimatedMinutes: t.estimateMinutes ?? '', aiEstimate: t.estimateMinutes != null, mentionsDate: !!t.mentionsDate, duplicateOf: t.duplicateOf || null,
  selected: !t.duplicateOf, fromAI: true,
});
const validDate = (v) => !v || (/^\d{4}-\d{2}-\d{2}$/.test(v) && Number(v.slice(0, 4)) >= 1900 && Number(v.slice(0, 4)) <= 2200);
const norm = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/** What goes to the AI, exactly as the server builds it. */
function SentBox({ sent, prompt }) {
  if (!sent) return null;
  return (
    <details style={{ marginTop: 6 }}>
      <summary style={{ cursor: 'pointer', fontSize: 12 }}>Qué recibe la IA</summary>
      <ul style={{ fontSize: 12, margin: '6px 0', paddingLeft: 18 }}>
        <li>Tu texto{sent.answers ? ` y ${sent.answers} respuesta${sent.answers === 1 ? '' : 's'}` : ''}.</li>
        <li>Los nombres de tus áreas: {sent.areas.join(', ')}.</li>
        {sent.goal ? <li>El objetivo «{sent.goal}»{sent.goalWhy ? ' y su porqué' : ''}.</li> : <li>Ningún objetivo.</li>}
        {sent.goalExcluded && <li>El objetivo elegido está marcado «fuera de la IA»: no se envía (las tareas pueden vincularse igual).</li>}
        {sent.goalTasks.length > 0 && <li>{sent.goalTasks.length === 1 ? 'El título de 1 tarea abierta' : `Los títulos de ${sent.goalTasks.length} tareas abiertas`} de ese objetivo.</li>}
        {sent.note && <li>El contexto que escribiste.</li>}
        <li>Nada más: ni tu calendario, ni tu correo, ni la biblioteca, ni datos de clientes, participantes o equipos.</li>
      </ul>
      {prompt && <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, background: 'rgba(30,42,44,0.04)', padding: 8, borderRadius: 6, maxHeight: 220, overflow: 'auto' }}>{prompt}</pre>}
    </details>
  );
}

function AreaPick({ row, areas, onChange }) {
  return (
    <select style={{ ...inputSt, width: 'auto' }} value={row.category || (row.newArea ? `new:${row.newArea}` : '')} aria-label="Área"
      onChange={e => { const v = e.target.value; onChange(v.startsWith('new:') ? { category: '', newArea: v.slice(4) } : { category: v, newArea: '' }); }}>
      <option value="">Sin área</option>
      {areas.filter(a => !a.archived).map(a => <option key={a.key} value={a.key}>{a.name}</option>)}
      {row.newArea && <option value={`new:${row.newArea}`}>Nueva: {row.newArea}</option>}
    </select>
  );
}

function TaskEditor({ row, index, count, areas, onChange, onMove, onRemove, confirmed, onConfirmArea }) {
  const set = (p) => onChange({ ...row, ...p });
  const minutes = row.estimatedMinutes === '' ? '' : String(row.estimatedMinutes);
  return (
    <div style={{ ...rowSt, opacity: row.selected ? 1 : 0.55 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="checkbox" checked={row.selected} onChange={e => set({ selected: e.target.checked })} aria-label={`Guardar «${row.title || 'tarea'}»`} />
        <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 22 }}>{index + 1}.</span>
        <input style={{ ...inputSt, flex: '1 1 240px', width: 'auto' }} value={row.title} maxLength={DL.taskTitle} onChange={e => set({ title: e.target.value })} aria-label={`Título del paso ${index + 1}`} />
        <span style={row.fromAI ? suggestionSt : badgeSt(false)}>{row.fromAI ? 'Sugerencia' : 'Agregada por ti'}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
        <AreaPick row={row} areas={areas} onChange={set} />
        <select style={{ ...inputSt, width: 'auto' }} value={row.priority} onChange={e => set({ priority: e.target.value })} aria-label="Prioridad">{TASK_PRIORITIES.map(p => <option key={p.key} value={p.key}>Prioridad {p.label.toLowerCase()}</option>)}</select>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>Fecha
          <input type="date" style={{ ...inputSt, width: 'auto' }} value={row.dueOn} onChange={e => set({ dueOn: e.target.value })} aria-label={`Fecha del paso ${index + 1}`} />
        </label>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>Estimación
          <input style={{ ...inputSt, width: 70 }} value={minutes} placeholder="min" inputMode="numeric" aria-label={`Estimación del paso ${index + 1}`}
            onChange={e => { const v = e.target.value; set({ estimatedMinutes: v === '' ? '' : (parseMinutes(v) ?? v), aiEstimate: false }); }} />
          {row.aiEstimate && <span style={mutedSt}>sugerida</span>}
        </label>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button className="action-btn" style={smallBtn} disabled={index === 0} onClick={() => onMove(-1)} aria-label="Subir">↑</button>
          <button className="action-btn" style={smallBtn} disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Bajar">↓</button>
          <button className="action-btn" style={smallBtn} onClick={onRemove}>Quitar</button>
        </span>
      </div>
      <textarea style={{ ...inputSt, marginTop: 6, minHeight: 34, fontFamily: 'inherit' }} value={row.notes} maxLength={DL.taskNotes} placeholder="Nota (opcional)" onChange={e => set({ notes: e.target.value })} aria-label={`Nota del paso ${index + 1}`} />
      {row.newArea && !row.category && (
        <div style={{ ...mutedSt, marginTop: 4 }}>
          La IA sugiere un área nueva: <b>{row.newArea}</b>.{' '}
          {confirmed.has(norm(row.newArea))
            ? <span style={{ color: 'var(--globant-green)' }}>Confirmada: se crea al guardar.</span>
            : <><button className="action-btn" style={smallBtn} onClick={() => onConfirmArea(row.newArea)}>Crear el área «{row.newArea}»</button> o elige una de las tuyas.</>}
        </div>
      )}
      {row.mentionsDate && <div style={{ ...mutedSt, color: '#9A6212', marginTop: 4 }}>El texto menciona una fecha o un plazo. No se guarda como fecha: la fecha la pones tú, si quieres.</div>}
      {row.duplicateOf && <div style={{ ...mutedSt, color: '#9A6212', marginTop: 4 }}>Ya tienes una tarea abierta igual: «{row.duplicateOf.title}». Quedó sin marcar.</div>}
      {!row.title.trim() && row.selected && <div style={{ ...mutedSt, color: '#9A6212' }}>Falta el título.</div>}
    </div>
  );
}

export default function PlanTasks({ today, onSaved, onClose, initialGoalId = '' }) {
  const [opts, setOpts] = useState(null);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('write');
  const [request, setRequest] = useState('');
  const [ctx, setCtx] = useState({ goalId: initialGoalId, includeWhy: false, includeTasks: false, note: '' });
  const [planId, setPlanId] = useState(null);
  const [sent, setSent] = useState(null);
  const [preview, setPreview] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [rows, setRows] = useState([]);
  const [aiNote, setAiNote] = useState('');
  const [checks, setChecks] = useState(null);
  const [confirmed, setConfirmed] = useState(new Set());
  const [goal, setGoal] = useState({ mode: initialGoalId ? 'existing' : 'none', id: initialGoalId, title: '', area: 'both' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inFlight = useRef(false);
  const nextManual = useRef(1);

  useEffect(() => { ownerCall({ action: 'planOptions' }).then(setOpts).catch(e => setError(e.message)); }, []);
  if (!opts) return <div style={cardSt}>{error ? <div style={warnSt}>{error}</div> : <div style={mutedSt}>Cargando…</div>}</div>;
  const areas = opts.areas || [];
  const goals = opts.goals || [];
  const chosenGoal = goals.find(g => g.id === ctx.goalId) || null;
  const context = { goalId: ctx.goalId || undefined, includeWhy: ctx.includeWhy, includeTasks: ctx.includeTasks, note: ctx.note };

  const once = async (fn) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e.message); }
    inFlight.current = false; setBusy(false);
  };
  const showSent = () => once(async () => {
    const r = await ownerCall({ action: 'proposePlan', dryRun: true, request, context, answers: answers.filter(a => a.answer.trim()), planId: planId || undefined });
    setPreview(r); setPlanId(r.planId);
  });
  const propose = (skipQuestions = false) => once(async () => {
    const r = await ownerCall({ action: 'proposePlan', request, context, answers: answers.filter(a => a.answer.trim()), skipQuestions, planId: planId || undefined });
    setPlanId(r.planId); setSent(r.sent); setPreview(null);
    if (r.proposal.mode === 'questions') {
      setQuestions(r.proposal.questions); setAnswers(r.proposal.questions.map(q => ({ question: q, answer: answers.find(a => a.question === q)?.answer || '' }))); setStage('questions');
      return;
    }
    setRows(r.proposal.tasks.map(rowFrom)); setAiNote(r.proposal.note || ''); setChecks(r.proposal.checks || null);
    if (chosenGoal) setGoal({ mode: 'existing', id: chosenGoal.id, title: '', area: 'both' });
    setStage('review');
  });

  const selected = rows.filter(r => r.selected);
  const needConfirm = [...new Set(selected.filter(r => r.newArea && !r.category && !confirmed.has(norm(r.newArea))).map(r => r.newArea))];
  const problems = [
    ...(!selected.length ? ['Marca al menos una tarea para guardar.'] : []),
    ...(selected.length > PLAN_SAVE_MAX ? [`Hasta ${PLAN_SAVE_MAX} tareas por vez.`] : []),
    ...(selected.some(r => !r.title.trim()) ? ['Hay una tarea marcada sin título.'] : []),
    ...(selected.some(r => !validDate(r.dueOn)) ? ['Hay una fecha inválida.'] : []),
    ...(selected.some(r => r.estimatedMinutes !== '' && !(Number(r.estimatedMinutes) >= 1 && Number(r.estimatedMinutes) <= 1440)) ? ['Una estimación no es válida (usa minutos, por ejemplo 45 o 1:30).'] : []),
    ...needConfirm.map(n => `Confirma el área nueva «${n}» o elige una de las tuyas.`),
    ...(goal.mode === 'existing' && !goal.id ? ['Elige el objetivo.'] : []),
    ...(goal.mode === 'create' && !goal.title.trim() ? ['Escribe el título del objetivo nuevo.'] : []),
    ...(!opts.ready ? ['Falta el campo Source en Personal Tasks (Airtable): sin él no se puede guardar. Ver docs/mi-espacio/configuracion.md.'] : []),
  ];
  const areaName = (r) => (r.category ? areas.find(a => a.key === r.category)?.name || 'Área' : r.newArea ? `${r.newArea} (nueva)` : 'Sin área');
  const byArea = selected.reduce((m, r) => { const n = areaName(r); m[n] = (m[n] || 0) + 1; return m; }, {});
  const dated = selected.filter(r => r.dueOn);

  const save = () => once(async () => {
    const r = await ownerCall({
      action: 'savePlan', planId, request, label: request.slice(0, PL.label), today,
      confirmedAreas: [...new Set(selected.filter(x => x.newArea && !x.category).map(x => x.newArea))].filter(n => confirmed.has(norm(n))),
      goal: goal.mode === 'existing' ? { id: goal.id } : goal.mode === 'create' ? { create: { title: goal.title, area: goal.area } } : null,
      tasks: selected.map(x => ({ key: x.key, title: x.title, notes: x.notes, category: x.category || null, newArea: x.category ? null : x.newArea || null, priority: x.priority, dueOn: x.dueOn || null, estimatedMinutes: x.estimatedMinutes === '' ? null : Number(x.estimatedMinutes) })),
    });
    setResult(r); setStage('saved');
    const fresh = await ownerCall({ action: 'planOptions' }).catch(() => null);
    if (fresh) setOpts(fresh);
    onSaved?.();
  });
  const move = (i, d) => { const next = [...rows]; [next[i], next[i + d]] = [next[i + d], next[i]]; setRows(next); };
  const reset = () => { setStage('write'); setRows([]); setQuestions([]); setAnswers([]); setPlanId(null); setSent(null); setPreview(null); setResult(null); setConfirmed(new Set()); setGoal({ mode: 'none', id: '', title: '', area: 'both' }); setChecks(null); setAiNote(''); };

  return (
    <section aria-label="De un objetivo a tareas" style={{ ...cardSt, borderColor: 'rgba(191,215,48,0.6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={labelSt}>De un objetivo a tareas</span>
        <span style={{ ...mutedSt, flex: 1 }}>{stage === 'write' ? '1 · Escribe' : stage === 'questions' ? '2 · Preguntas' : stage === 'review' ? '3 · Revisa y elige' : '4 · Guardadas'}</span>
        {onClose && <button className="action-btn" style={smallBtn} onClick={onClose}>Cerrar</button>}
      </div>
      {error && <div style={{ ...warnSt, padding: 8 }}>{error}</div>}

      {stage === 'write' && (
        <>
          <label style={{ ...mutedSt, display: 'block', marginTop: 6 }} htmlFor="plan-request">¿Qué quieres conseguir o hacer?</label>
          <textarea id="plan-request" style={{ ...inputSt, minHeight: 70, fontFamily: 'inherit' }} maxLength={PL.request} value={request} onChange={e => setRequest(e.target.value)}
            placeholder="Por ejemplo: quiero terminar la primera versión de mi método y probarlo en un taller piloto." />
          <div style={{ marginTop: 8 }}>
            <span style={{ ...mutedSt, display: 'block' }}>Contexto que puede ver la IA (solo lo que elijas)</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
              <select style={{ ...inputSt, width: 'auto', maxWidth: '100%' }} value={ctx.goalId} onChange={e => setCtx({ ...ctx, goalId: e.target.value, includeWhy: false, includeTasks: false })} aria-label="Objetivo relacionado">
                <option value="">Sin objetivo</option>
                {goals.map(g => <option key={g.id} value={g.id}>{g.title}{g.excludeFromAI ? ' (fuera de la IA)' : ''}</option>)}
              </select>
              {chosenGoal && !chosenGoal.excludeFromAI && <>
                {chosenGoal.hasWhy && <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={ctx.includeWhy} onChange={e => setCtx({ ...ctx, includeWhy: e.target.checked })} /> su porqué</label>}
                <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={ctx.includeTasks} onChange={e => setCtx({ ...ctx, includeTasks: e.target.checked })} /> los títulos de sus tareas abiertas (para no repetirlas)</label>
              </>}
            </div>
            {chosenGoal?.excludeFromAI && <div style={mutedSt}>Este objetivo está «fuera de la IA»: no se le envía. Podrás vincular las tareas igual.</div>}
            <textarea style={{ ...inputSt, marginTop: 6, minHeight: 40, fontFamily: 'inherit' }} maxLength={PL.note} value={ctx.note} onChange={e => setCtx({ ...ctx, note: e.target.value })} placeholder="Contexto que quieras agregar (opcional)" aria-label="Contexto que agrego" />
          </div>
          <Notice icon="✨" tone="ai">La IA propone y tú decides: <b>nada se guarda hasta que confirmes</b>. No pone fechas ni plazos, no reserva horarios y no envía mensajes. No escribas aquí datos privados de clientes o participantes.</Notice>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="action-btn btn-primary" disabled={busy || !request.trim()} onClick={() => propose(false)}>{busy ? 'Pensando…' : 'Proponer tareas'}</button>
            <button className="action-btn" disabled={busy || !request.trim()} onClick={showSent}>Ver qué se envía</button>
          </div>
          {preview && <SentBox sent={preview.sent} prompt={preview.prompt} />}
        </>
      )}

      {stage === 'questions' && (
        <>
          <div style={{ ...mutedSt, marginTop: 6 }}>Para proponer algo útil, la IA necesita un poco más. Responde lo que quieras (son breves):</div>
          {questions.map((q, i) => (
            <div key={q} style={{ margin: '8px 0' }}>
              <label style={{ fontSize: 13, display: 'block' }} htmlFor={`plan-q-${i}`}>{q}</label>
              <input id={`plan-q-${i}`} style={inputSt} maxLength={PL.answer} value={answers[i]?.answer || ''} onChange={e => setAnswers(answers.map((a, j) => (j === i ? { ...a, answer: e.target.value } : a)))} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="action-btn btn-primary" disabled={busy || !answers.some(a => a.answer.trim())} onClick={() => propose(false)}>Proponer con mis respuestas</button>
            <button className="action-btn" disabled={busy} onClick={() => propose(true)}>Proponer sin responder</button>
            <button className="action-btn" disabled={busy} onClick={() => setStage('write')}>Volver a mi texto</button>
          </div>
          <SentBox sent={sent} />
        </>
      )}

      {stage === 'review' && (
        <>
          <Notice icon="💡" tone="ai"><b>Esto es una sugerencia: todavía no se guardó nada.</b> Edita, quita, agrega, ordena o cambia el área de cada tarea, y marca las que quieres guardar.</Notice>
          <div style={{ fontSize: 13, margin: '4px 0' }}><b>Tu objetivo:</b> {request}</div>
          {aiNote && <div style={mutedSt}>Nota de la IA: {aiNote}</div>}
          {checks && (checks.trimmed > 0 || checks.invalidAreas > 0 || checks.repeated > 0) && (
            <div style={mutedSt}>Controles: {[checks.trimmed && `${checks.trimmed} tareas de más no se muestran`, checks.repeated && `${checks.repeated} repetidas quitadas`, checks.invalidAreas && `${checks.invalidAreas} áreas inválidas quedaron sin área`].filter(Boolean).join(' · ')}.</div>
          )}
          {!rows.length && <div style={mutedSt}>La IA no propuso tareas. Agrega las tuyas o vuelve a tu texto.</div>}
          {rows.map((r, i) => (
            <TaskEditor key={r.key} row={r} index={i} count={rows.length} areas={areas} confirmed={confirmed}
              onChange={(v) => setRows(rows.map(x => (x.key === r.key ? v : x)))}
              onMove={(d) => move(i, d)} onRemove={() => setRows(rows.filter(x => x.key !== r.key))}
              onConfirmArea={(n) => setConfirmed(new Set([...confirmed, norm(n)]))} />
          ))}
          <button className="action-btn" style={smallBtn} disabled={rows.length >= PLAN_SAVE_MAX} onClick={() => { const k = `m${nextManual.current++}`; setRows([...rows, { key: k, title: '', notes: '', category: '', newArea: '', priority: 'normal', dueOn: '', estimatedMinutes: '', aiEstimate: false, mentionsDate: false, duplicateOf: null, selected: true, fromAI: false }]); }}>+ Agregar una tarea</button>

          <div style={{ ...rowSt, background: 'rgba(30,42,44,0.03)' }}>
            <span style={labelSt}>Vincular al objetivo</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
              {[['none', 'Sin vincular'], ['existing', 'A un objetivo mío'], ['create', 'Crear un objetivo con este texto']].map(([k, l]) => (
                <label key={k} style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="radio" name="plan-goal" checked={goal.mode === k} onChange={() => setGoal({ ...goal, mode: k, id: k === 'existing' ? (goal.id || ctx.goalId) : goal.id, title: k === 'create' ? (goal.title || request.slice(0, 200)) : goal.title })} /> {l}
                </label>
              ))}
            </div>
            {goal.mode === 'existing' && (
              <select style={{ ...inputSt, width: 'auto', maxWidth: '100%', marginTop: 6 }} value={goal.id} onChange={e => setGoal({ ...goal, id: e.target.value })} aria-label="Objetivo">
                <option value="">Elegir…</option>
                {goals.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
              </select>
            )}
            {goal.mode === 'create' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                <input style={{ ...inputSt, flex: '1 1 260px', width: 'auto' }} maxLength={200} value={goal.title} onChange={e => setGoal({ ...goal, title: e.target.value })} aria-label="Título del objetivo nuevo" />
                <select style={{ ...inputSt, width: 'auto' }} value={goal.area} onChange={e => setGoal({ ...goal, area: e.target.value })} aria-label="Tipo de objetivo">{GOAL_AREAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}</select>
              </div>
            )}
          </div>

          <div style={{ ...rowSt, borderColor: 'rgba(91,191,181,0.5)' }}>
            <span style={labelSt}>Antes de guardar</span>
            <ul style={{ fontSize: 13, margin: '4px 0', paddingLeft: 18 }}>
              <li>Se guardan <b>{selected.length}</b> de {rows.length} tareas, en este orden, como tareas tuyas de siempre.</li>
              {selected.length > 0 && <li>Por área: {Object.entries(byArea).map(([n, c]) => `${n} (${c})`).join(' · ')}.</li>}
              <li>{dated.length ? `${dated.length} con fecha: aparecen en Mi día ese día.` : 'Ninguna con fecha.'} {selected.length - dated.length > 0 ? `${selected.length - dated.length} sin fecha: quedan en «Sin fecha y próximas».` : ''}</li>
              <li>{goal.mode === 'existing' ? `Vinculadas a «${goals.find(g => g.id === goal.id)?.title || '…'}».` : goal.mode === 'create' ? `Se crea el objetivo «${goal.title}» y quedan vinculadas.` : 'Sin objetivo vinculado.'}</li>
              {selected.some(r => r.newArea && !r.category && confirmed.has(norm(r.newArea))) && <li>Se crean las áreas confirmadas: {[...new Set(selected.filter(r => r.newArea && !r.category).map(r => r.newArea))].join(', ')}.</li>}
              <li>No se reserva ningún horario ni se envía ningún mensaje.</li>
            </ul>
            {problems.length > 0 && <ul style={{ fontSize: 12, margin: '4px 0', paddingLeft: 18, color: '#9A6212' }}>{problems.map(p => <li key={p}>{p}</li>)}</ul>}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="action-btn btn-primary" disabled={busy || problems.length > 0} onClick={save}>{busy ? 'Guardando…' : `Guardar ${selected.length} tarea${selected.length === 1 ? '' : 's'}`}</button>
              <button className="action-btn" disabled={busy} onClick={() => setStage('write')}>Volver a mi texto</button>
            </div>
          </div>
          <SentBox sent={sent} />
        </>
      )}

      {stage === 'saved' && result && (
        <>
          <Notice icon="✅" tone="private">
            Guardadas {result.tasks.length} tareas{result.already.length ? ` (${result.already.length} ya estaban guardadas: no se duplicaron)` : ''}.
            {result.goalCreated ? ' Se creó el objetivo.' : ''}{result.areasCreated.length ? ` Se creó el área ${result.areasCreated.join(', ')}.` : ''}
          </Notice>
          {Object.entries(result.tasks.reduce((m, t) => { const n = areas.find(a => a.key === t.category)?.name || 'Sin área'; (m[n] = m[n] || []).push(t); return m; }, {})).map(([n, list]) => (
            <div key={n} style={{ margin: '8px 0' }}>
              <b style={{ fontSize: 13 }}>{n}</b>
              {list.map(t => (
                <div key={t.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13, margin: '4px 0 4px 8px' }}>
                  <span style={savedSt}>Guardada</span>
                  <span style={mutedSt}>Paso {t.source?.order} de {t.source?.of}</span>
                  <span>{t.title}</span>
                  {t.dueOn ? <span style={mutedSt}>· en Mi día el {fmtDate(t.dueOn)}</span> : <span style={mutedSt}>· sin fecha</span>}
                  {t.estimatedMinutes ? <span style={mutedSt}>· {t.estimatedMinutes} min</span> : null}
                </div>
              ))}
            </div>
          ))}
          <button className="action-btn" onClick={reset}>Planificar otro objetivo</button>
        </>
      )}
    </section>
  );
}
